import type { ConsentState } from "./analyticsTypes";
import { saveAppSetting } from "../db/appSettingsRepo";

export const ANALYTICS_NOTICE_VERSION =
  import.meta.env.VITE_ANALYTICS_NOTICE_VERSION || "analytics-notice-v1-draft";
export const ANALYTICS_MAX_EVENTS = 500;
export const ANALYTICS_MAX_QUEUE_BYTES = 1024 * 1024;
export const ANALYTICS_MAX_BATCH_EVENTS = 25;
export const ANALYTICS_MAX_BATCH_BYTES = 50 * 1024;

const ANALYTICS_DISABLED_KEY = "moveinreport.analytics.disabled";
const ANALYTICS_QA_KEY = "moveinreport.analytics.internalQa";
export const ANALYTICS_CONSENT_STATE_KEY = "analyticsConsentState";
export const ANALYTICS_NOTICE_VERSION_KEY = "analyticsNoticeVersion";
export const ANALYTICS_CHOICE_UPDATED_AT_KEY = "analyticsChoiceUpdatedAt";
export const ANALYTICS_CONSENT_CHANGED_EVENT = "moveinreport:analytics-consent-changed";

export type AnalyticsConsentState = "unknown" | "allowed" | "declined" | "internal_qa";
export type PublicAnalyticsConsentChoice = "allowed" | "declined";

export interface AnalyticsConsentSnapshot {
  analyticsConsentState: AnalyticsConsentState;
  analyticsNoticeVersion?: string;
  analyticsChoiceUpdatedAt?: string;
  effectiveConsentState: AnalyticsConsentState;
  isPublicAnalyticsAvailable: boolean;
  isInternalQa: boolean;
  canSendOptionalAnalytics: boolean;
  shouldRequestChoice: boolean;
}

export interface AnalyticsDiagnosticsSnapshot {
  publicAnalyticsAvailable: boolean;
  internalQaBuildAvailable: boolean;
  internalQaMode: boolean;
  locallyDisabled: boolean;
  canSendFromPublicAppPages: boolean;
  noticeVersion: string;
}

export function applyAnalyticsQueryFlags(): void {
  const params = new URLSearchParams(window.location.search);
  const mode = params.get("mir_analytics");
  if (mode === "qa") {
    safeSetLocalStorage(ANALYTICS_QA_KEY, "true");
    safeSetLocalStorage(ANALYTICS_DISABLED_KEY, "false");
  } else if (mode === "off") {
    safeSetLocalStorage(ANALYTICS_DISABLED_KEY, "true");
    writeAnalyticsConsentPreference("declined", new Date().toISOString());
  }
}

export function isAnalyticsEnabled(): boolean {
  if (safeGetLocalStorage(ANALYTICS_DISABLED_KEY) === "true" || isAdminRoute()) {
    return false;
  }
  if (isInternalQaAnalyticsEnabled()) {
    return true;
  }
  if (!isPublicAnalyticsAvailable()) {
    return false;
  }
  const snapshot = readAnalyticsConsentPreference();
  return snapshot.analyticsConsentState === "allowed" && snapshot.analyticsNoticeVersion === ANALYTICS_NOTICE_VERSION;
}

export function getConsentState(): ConsentState {
  if (isInternalQaAnalyticsEnabled() && !isAdminRoute()) {
    return "internal_qa";
  }
  const snapshot = readAnalyticsConsentPreference();
  if (snapshot.analyticsNoticeVersion !== ANALYTICS_NOTICE_VERSION) {
    return "not_shown";
  }
  if (snapshot.analyticsConsentState === "allowed") {
    return "accepted";
  }
  if (snapshot.analyticsConsentState === "declined") {
    return "opted_out";
  }
  return "not_shown";
}

export function isInternalQaMode(): boolean {
  return safeGetLocalStorage(ANALYTICS_QA_KEY) === "true";
}

export function setInternalQaAnalyticsMode(enabled: boolean): AnalyticsDiagnosticsSnapshot {
  if (enabled) {
    safeSetLocalStorage(ANALYTICS_QA_KEY, "true");
    safeSetLocalStorage(ANALYTICS_DISABLED_KEY, "false");
  } else {
    safeSetLocalStorage(ANALYTICS_QA_KEY, "false");
  }
  dispatchConsentChange();
  return getAnalyticsDiagnosticsSnapshot();
}

export function getAnalyticsDiagnosticsSnapshot(): AnalyticsDiagnosticsSnapshot {
  const preference = readAnalyticsConsentPreference();
  const publicConsentReady =
    preference.analyticsConsentState === "allowed" &&
    preference.analyticsNoticeVersion === ANALYTICS_NOTICE_VERSION;
  const internalQaBuildAvailable = import.meta.env.VITE_ANALYTICS_INTERNAL_QA === "true";
  const locallyDisabled = safeGetLocalStorage(ANALYTICS_DISABLED_KEY) === "true";
  return {
    publicAnalyticsAvailable: isPublicAnalyticsAvailable(),
    internalQaBuildAvailable,
    internalQaMode: isInternalQaMode(),
    locallyDisabled,
    canSendFromPublicAppPages:
      !locallyDisabled &&
      (
        (internalQaBuildAvailable && isInternalQaMode()) ||
        (isPublicAnalyticsAvailable() && publicConsentReady)
      ),
    noticeVersion: ANALYTICS_NOTICE_VERSION
  };
}

export function isPublicAnalyticsAvailable(): boolean {
  return import.meta.env.VITE_ANALYTICS_ENABLED === "true";
}

export function shouldShowAnalyticsConsent(): boolean {
  return getAnalyticsConsentSnapshot().shouldRequestChoice;
}

export function getAnalyticsConsentSnapshot(): AnalyticsConsentSnapshot {
  const preference = readAnalyticsConsentPreference();
  const isInternalQa = isInternalQaAnalyticsEnabled() && !isAdminRoute();
  const hasCurrentPublicChoice =
    (preference.analyticsConsentState === "allowed" || preference.analyticsConsentState === "declined") &&
    preference.analyticsNoticeVersion === ANALYTICS_NOTICE_VERSION;
  const canSendOptionalAnalytics = isAnalyticsEnabled();

  return {
    ...preference,
    effectiveConsentState: isInternalQa ? "internal_qa" : preference.analyticsConsentState,
    isPublicAnalyticsAvailable: isPublicAnalyticsAvailable(),
    isInternalQa,
    canSendOptionalAnalytics,
    shouldRequestChoice:
      safeGetLocalStorage(ANALYTICS_DISABLED_KEY) !== "true" &&
      !isAdminRoute() &&
      !isInternalQa &&
      isPublicAnalyticsAvailable() &&
      !hasCurrentPublicChoice
  };
}

export function setAnalyticsConsentChoice(choice: PublicAnalyticsConsentChoice): AnalyticsConsentSnapshot {
  const updatedAt = new Date().toISOString();
  safeSetLocalStorage(ANALYTICS_DISABLED_KEY, "false");
  writeAnalyticsConsentPreference(choice, updatedAt);
  dispatchConsentChange();
  return getAnalyticsConsentSnapshot();
}

export function getAppVersion(): string {
  return import.meta.env.VITE_APP_VERSION || "0.1.0";
}

export function getBuildHash(): string | undefined {
  const value = import.meta.env.VITE_BUILD_HASH;
  return typeof value === "string" && value.trim() ? value.trim().slice(0, 80) : undefined;
}

function readAnalyticsConsentPreference(): Pick<
  AnalyticsConsentSnapshot,
  "analyticsConsentState" | "analyticsNoticeVersion" | "analyticsChoiceUpdatedAt"
> {
  return {
    analyticsConsentState: normalizeConsentState(safeGetLocalStorage(ANALYTICS_CONSENT_STATE_KEY)),
    analyticsNoticeVersion: safeGetLocalStorage(ANALYTICS_NOTICE_VERSION_KEY) ?? undefined,
    analyticsChoiceUpdatedAt: safeGetLocalStorage(ANALYTICS_CHOICE_UPDATED_AT_KEY) ?? undefined
  };
}

function writeAnalyticsConsentPreference(choice: PublicAnalyticsConsentChoice, updatedAt: string): void {
  safeSetLocalStorage(ANALYTICS_CONSENT_STATE_KEY, choice);
  safeSetLocalStorage(ANALYTICS_NOTICE_VERSION_KEY, ANALYTICS_NOTICE_VERSION);
  safeSetLocalStorage(ANALYTICS_CHOICE_UPDATED_AT_KEY, updatedAt);

  void Promise.all([
    saveAppSetting(ANALYTICS_CONSENT_STATE_KEY, choice, updatedAt),
    saveAppSetting(ANALYTICS_NOTICE_VERSION_KEY, ANALYTICS_NOTICE_VERSION, updatedAt),
    saveAppSetting(ANALYTICS_CHOICE_UPDATED_AT_KEY, updatedAt, updatedAt)
  ]).catch(() => {
    // Consent already has a synchronous local copy; IndexedDB mirroring is best-effort.
  });
}

function normalizeConsentState(value: string | null): AnalyticsConsentState {
  return value === "allowed" || value === "declined" ? value : "unknown";
}

function isInternalQaAnalyticsEnabled(): boolean {
  return (
    safeGetLocalStorage(ANALYTICS_DISABLED_KEY) !== "true" &&
    import.meta.env.VITE_ANALYTICS_INTERNAL_QA === "true" &&
    isInternalQaMode()
  );
}

function isAdminRoute(): boolean {
  try {
    return window.location.pathname === "/admin" || window.location.pathname.startsWith("/admin/");
  } catch {
    return false;
  }
}

function dispatchConsentChange(): void {
  try {
    window.dispatchEvent(new CustomEvent(ANALYTICS_CONSENT_CHANGED_EVENT));
  } catch {
    // UI synchronization is best-effort.
  }
}

function safeGetLocalStorage(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSetLocalStorage(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Analytics flags are best-effort and must never affect capture.
  }
}
